import { useEffect, useState } from 'react';
import to from 'await-to-js';
import apiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import type { Campaign } from 'c-sdk';
import { usesInsertionOrderTargeting } from 'c-main/Lib';

/**
 * True when a `vistar_us2` campaign has no insertion orders attached, so a CAD run has nothing to
 * build. The CAD run buttons are disabled on it.
 *
 * Asks the existing `GET campaign/{id}/insertion-orders` and only for the targeting type; every
 * other type makes no request and gets `false`. A failed request also gives `false`: the API refuses
 * the run with a 422 anyway, so the button is a convenience, not the guard.
 *
 * Re-checked whenever the window regains focus, so attaching insertion orders on the schedule page
 * (often in another tab) re-enables the button without a reload.
 */
const useInsertionOrdersMissing = (campaign?: Pick<Campaign, 'id' | 'type'> | null): boolean => {
    const applies = usesInsertionOrderTargeting(campaign);
    const id = campaign?.id;
    const { start } = useAPIClientRequest(apiClient.Entities.Campaign.insertionOrders);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        if (!applies || id == null) {
            setMissing(false);
            return undefined;
        }

        let cancelled = false;

        const check = async () => {
            const [err, res] = await to(start(id));

            if (!cancelled) {
                setMissing(!err && Array.isArray(res?.data?.data) && res.data.data.length === 0);
            }
        };

        check();
        window.addEventListener('focus', check);

        return () => {
            cancelled = true;
            window.removeEventListener('focus', check);
        };
    }, [applies, id, start]);

    return missing;
};

export default useInsertionOrdersMissing;
